/**
 * Typed API client.
 *
 * Access tokens are short-lived, so a session is kept alive with the refresh
 * token. When a request comes back 401, the client refreshes once and replays
 * the original request rather than throwing the user out to the login page --
 * a fifteen-minute access token should not end a session mid-task.
 */

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

const ACCESS_KEY = 'schemabee.access_token';
const REFRESH_KEY = 'schemabee.refresh_token';

export const tokens = {
  access: () => localStorage.getItem(ACCESS_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  save(accessToken: string, refreshToken: string) {
    localStorage.setItem(ACCESS_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

// --- Types ------------------------------------------------------------------

export type User = {
  id: string;
  email: string;
  name: string;
  created_at?: string;
};

export type TokenResponse = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  access_expires_at: string;
  user: User;
};

export type RecaptchaConfig = { enabled: boolean; site_key: string | null };

export type DatabaseType = 'postgresql' | 'mysql' | 'mariadb' | 'sqlite' | 'sqlserver';

export type Connection = {
  id: string;
  name: string;
  database_type: string;
  host: string | null;
  port: number | null;
  database_name: string;
  username: string | null;
  ssl_mode: string | null;
  has_password: boolean;
  status: 'unknown' | 'connected' | 'error';
  last_tested_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
};

export type ConnectionInput = {
  name: string;
  /**
   * Details may be given as individual fields or as one connection string, but
   * never both: the server rejects a request that mixes them.
   */
  database_type?: DatabaseType;
  host?: string;
  port?: number;
  database_name?: string;
  username?: string;
  password?: string;
  ssl_mode?: string;
  connection_url?: string;
};

export type TestResult = {
  success: boolean;
  message: string;
  server_version?: string | null;
  latency_ms?: number | null;
};

export type ColumnInfo = {
  name: string;
  type: string;
  nullable: boolean;
  primary_key: boolean;
  unique: boolean;
  indexes: string[];
  references: string[];
  default: string | null;
};

export type QueryResult = {
  columns: string[];
  rows: (string | number | boolean | null)[][];
  row_count: number;
  execution_time_ms: number;
  truncated: boolean;
  truncation_reason: string | null;
};

export type McpToken = {
  id: string;
  name: string;
  scope: 'read_only' | 'read_write';
  token_hint: string;
  revoked: boolean;
  expired: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string | null;
  /** The databases this token can reach, or null when it can reach all of them. */
  connection_ids: string[] | null;
  all_connections: boolean;
  allow_database_tools: boolean;
  allow_pdf_generation: boolean;
  allow_docx_generation: boolean;
  allow_pptx_generation: boolean;
  allow_txt_generation: boolean;
  allow_md_generation: boolean;
  allow_html_generation: boolean;
  allow_json_generation: boolean;
  allow_csv_generation: boolean;
};

export type CreatedMcpToken = McpToken & { token: string };

export type McpServerInfo = {
  server_url: string;
  config_example: string;
  instructions: string;
};

// --- Transport --------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const detail = body?.detail;
    if (typeof detail === 'string') return detail;
    // FastAPI validation errors arrive as a list of field problems.
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((item: { loc?: string[]; msg?: string }) =>
          `${item.loc?.slice(1).join('.') ?? ''} ${item.msg ?? ''}`.trim(),
        )
        .join('; ');
    }
    return body?.message ?? `Request failed (${response.status})`;
  } catch {
    return `Request failed (${response.status})`;
  }
}

/** Notified when the session cannot be recovered, so the app can sign out. */
let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(handler: (() => void) | null) {
  onSessionExpired = handler;
}

/**
 * In-flight refresh, shared so that several concurrent 401s trigger one
 * refresh rather than a stampede that rotates the same token twice -- the
 * second would be rejected, since refresh tokens are single-use.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = tokens.refresh();
    if (!refreshToken) return null;
    try {
      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!response.ok) return null;
      const body: TokenResponse = await response.json();
      tokens.save(body.access_token, body.refresh_token);
      return body.access_token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function request<T>(path: string, options: RequestInit = {}, retry = true): Promise<T> {
  const access = tokens.access();
  const response = await fetch(`${API_BASE}/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
      ...options.headers,
    },
  });

  if (response.status === 401 && retry) {
    const renewed = await refreshAccessToken();
    if (renewed) return request<T>(path, options, false);
    tokens.clear();
    onSessionExpired?.();
  }

  if (!response.ok) throw new ApiError(await readError(response), response.status);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const body = (data: unknown) => JSON.stringify(data);

// --- Endpoints --------------------------------------------------------------

export const api = {
  auth: {
    recaptchaConfig: () => request<RecaptchaConfig>('/auth/recaptcha-config'),
    register: (input: { email: string; name: string; password: string; captcha_token?: string }) =>
      request<TokenResponse>('/auth/register', { method: 'POST', body: body(input) }),
    login: (input: { email: string; password: string; captcha_token?: string }) =>
      request<TokenResponse>('/auth/login', { method: 'POST', body: body(input) }),
    me: () => request<User>('/auth/me'),
    logout: (allSessions = false) =>
      request<{ detail: string }>('/auth/logout', {
        method: 'POST',
        body: body({ refresh_token: tokens.refresh(), all_sessions: allSessions }),
      }),
  },

  databases: {
    list: () => request<Connection[]>('/databases'),
    get: (id: string) => request<Connection>(`/databases/${id}`),
    create: (input: ConnectionInput) =>
      request<Connection>('/databases', { method: 'POST', body: body(input) }),
    update: (id: string, input: Partial<ConnectionInput>) =>
      request<Connection>(`/databases/${id}`, { method: 'PATCH', body: body(input) }),
    remove: (id: string) => request<void>(`/databases/${id}`, { method: 'DELETE' }),
    test: (input: ConnectionInput) =>
      request<TestResult>('/databases/test', { method: 'POST', body: body(input) }),
    retest: (id: string) =>
      request<TestResult>(`/databases/${id}/test`, { method: 'POST' }),
    tables: (id: string) =>
      request<{ tables: string[]; views: string[]; schemas: string[] }>(
        `/databases/${id}/tables`,
      ),
    columns: (id: string, table: string) =>
      request<{ database_id: string; table_name: string; columns: ColumnInfo[] }>(
        `/databases/${id}/tables/${encodeURIComponent(table)}/columns`,
      ),
  },

  queries: {
    run: (connectionId: string, query: string) =>
      request<QueryResult>('/queries/execute', {
        method: 'POST',
        body: body({ connection_id: connectionId, query }),
      }),
    history: () => request<QueryHistoryEntry[]>('/queries/history'),
  },

  mcp: {
    tokens: () => request<McpToken[]>('/mcp-tokens'),
    createToken: (input: {
      name: string;
      scope: string;
      expires_in_days?: number;
      /** Omit for every database; [] grants no database access. */
      connection_ids?: string[] | null;
      allow_database_tools?: boolean;
      allow_pdf_generation?: boolean;
      allow_docx_generation?: boolean;
      allow_pptx_generation?: boolean;
      allow_txt_generation?: boolean;
      allow_md_generation?: boolean;
      allow_html_generation?: boolean;
      allow_json_generation?: boolean;
      allow_csv_generation?: boolean;
    }) => request<CreatedMcpToken>('/mcp-tokens', { method: 'POST', body: body(input) }),
    /** Change which databases an existing token can reach, without reissuing it. */
    updateTokenScope: (id: string, connectionIds: string[] | null) =>
      request<McpToken>(`/mcp-tokens/${id}`, {
        method: 'PATCH',
        body: body({ connection_ids: connectionIds }),
      }),
    revokeToken: (id: string) =>
      request<void>(`/mcp-tokens/${id}`, { method: 'DELETE' }),
    serverInfo: () => request<McpServerInfo>('/mcp-tokens/server-info'),
  },
};

export type QueryHistoryEntry = {
  id: string;
  connection_id: string;
  query: string;
  success: boolean;
  row_count: number;
  execution_time_ms: number;
  error_message: string | null;
  created_at: string;
};
